import { errorCodes, getUserFriendlyErrorMessage } from '@/utils/errormessages'
import { validateEmail, isDuplicatedLicenses } from '@/utils/common'
import { checkAllLicensesFields } from '@/utils/commonMembersPage'
import type { LegacyLicense } from '../helpers/licenseShapeAdapter'

/**
 * Typed reproduction of the validation gate inside the legacy `onClickSafeMemberButton`.
 * The create page now drives its own submit, so the validation is extracted here; field-level
 * error setters are passed in so the same per-field messages surface as before. Returns whether
 * the form is valid (caller proceeds to the create mutation only when `true`) — equivalent to
 * the old "else-branch dispatched the create thunk" control flow.
 */

export interface ValidateCreateMemberInput {
  firstName: string
  lastName: string
  email: string
  role: string
  professionalTitle: string
  phoneNumberError: string | boolean
  licensesArray: LegacyLicense[]
  isProviderWithLicense: boolean
}

export interface ValidateCreateMemberSetters {
  setPageError: (code?: number, extra?: unknown) => void
  setFirstNameLocalError: (msg: string) => void
  setFirstNameError: (val: boolean) => void
  setLastNameLocalError: (msg: string) => void
  setLastNameError: (val: boolean) => void
  setEmailLocalError: (msg: string) => void
  setEmailError: (val: boolean) => void
  setRoleLocalError: (msg: string) => void
  setRoleError: (val: boolean) => void
  setProfessionalTitleLocalError: (msg: string) => void
  setProfessionalTitleError: (val: boolean) => void
  setLicensesArray: (next: LegacyLicense[] | ((prev: LegacyLicense[]) => LegacyLicense[])) => void
}

export const validateCreateMember = (
  input: ValidateCreateMemberInput,
  setters: ValidateCreateMemberSetters,
): boolean => {
  const {
    firstName,
    lastName,
    email,
    role,
    professionalTitle,
    phoneNumberError,
    licensesArray,
    isProviderWithLicense,
  } = input

  const hasError =
    firstName?.trim() === '' ||
    (professionalTitle?.trim() === '' && role === 'Provider') ||
    lastName?.trim() === '' ||
    email?.trim() === '' ||
    validateEmail(email) === null ||
    !email?.trim().toLowerCase().endsWith('@quick.md') ||
    role?.trim() === '' ||
    (Boolean(checkAllLicensesFields(licensesArray, setters.setLicensesArray)) &&
      role === 'Provider' &&
      isProviderWithLicense) ||
    (isDuplicatedLicenses(licensesArray) && role === 'Provider' && isProviderWithLicense) ||
    Boolean(phoneNumberError)

  if (!hasError) {
    if (isProviderWithLicense) {
      setters.setLicensesArray(
        licensesArray.map((t) => ({ ...t, licenseNumberError: false, licenseNumberLocalError: '' })),
      )
    }
    return true
  }

  setters.setPageError(errorCodes.FILL_ALL_FIELDS)

  if (firstName?.trim() === '') {
    setters.setFirstNameLocalError(getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'First name'))
    setters.setFirstNameError(true)
  }
  if (professionalTitle?.trim() === '' && role === 'Provider') {
    setters.setProfessionalTitleLocalError(
      getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Professional title'),
    )
    setters.setProfessionalTitleError(true)
  }
  if (lastName?.trim() === '') {
    setters.setLastNameLocalError(getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Last name'))
    setters.setLastNameError(true)
  }
  if (role?.trim() === '') {
    setters.setRoleLocalError(getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Role'))
    setters.setRoleError(true)
  }
  if (
    (checkAllLicensesFields(licensesArray, setters.setLicensesArray) || isDuplicatedLicenses(licensesArray)) &&
    role === 'Provider'
  ) {
    checkAllLicensesFields(licensesArray, setters.setLicensesArray)
  }
  if (validateEmail(email) === null) {
    setters.setEmailLocalError(getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, 'Email'))
    setters.setEmailError(true)
  } else if (!email?.trim().toLowerCase().endsWith('@quick.md')) {
    setters.setEmailLocalError('Email must use the @quick.md domain')
    setters.setEmailError(true)
  }
  if (email?.trim() === '') {
    setters.setEmailLocalError(getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Email'))
    setters.setEmailError(true)
  }

  return false
}
